import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Clock, ArrowRight, Compass } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageIntro from "@/components/stagiaire/PageIntro";
import { useStagiaireActions } from "@/components/stagiaire/StagiaireActionsContext";

const TABS = [
    { key: "upcoming", label: "À venir" },
    { key: "past", label: "Passées" },
    { key: "all", label: "Toutes" },
];

function formatDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

export default function StagiaireSessions() {
    const [items, setItems] = useState([]);
    const [tab, setTab] = useState("upcoming");
    const [loading, setLoading] = useState(true);
    const { data: actions } = useStagiaireActions();
    const sessionsActionCount = actions?.counters?.sessions || 0;

    useEffect(() => {
        api.get("/stagiaire/sessions")
            .then((r) => setItems(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, []);

    const today = new Date().toISOString().slice(0, 10);

    const filtered = useMemo(() => {
        if (tab === "all") return items;
        if (tab === "upcoming") return items.filter((it) => it.session.start_date >= today);
        return items.filter((it) => it.session.start_date < today);
    }, [items, tab, today]);

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="page-stagiaire-sessions">
            <PageIntro
                eyebrow="Mes sessions"
                title="Toutes mes formations."
                description="Retrouvez ici toutes les sessions auxquelles vous êtes inscrit(e), passées et à venir. Cliquez sur une session pour consulter le détail, signer votre émargement ou compléter vos évaluations."
                tips={[
                    "Badge orange « Confirmé » = inscription validée, place réservée.",
                    "Badge vert « Présent » = présence effective validée par l'admin et attestation disponible.",
                    "Badge gris « Passée » = session terminée, en attente de validation de présence.",
                ]}
                actionCount={sessionsActionCount}
                actionSectionKey="sessions"
            />

            {/* CTA — Découvrir les sessions disponibles */}
            <section
                className="mb-8 flex flex-col gap-4 rounded-2xl border border-brand-line bg-white p-6 shadow-premium sm:flex-row sm:items-center sm:justify-between"
                data-testid="stagiaire-discover-sessions"
            >
                <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                        <Compass className="h-5 w-5" />
                    </div>
                    <div>
                        <div className="font-display text-lg text-brand-ink">Envie d&apos;une nouvelle formation ?</div>
                        <div className="mt-1 text-sm text-brand-smoke">
                            Découvrez toutes les sessions ouvertes à l&apos;inscription, filtrables par cursus, ville et date.
                        </div>
                    </div>
                </div>
                <Link
                    to="/sessions"
                    data-testid="stagiaire-discover-sessions-cta"
                    className="inline-flex flex-shrink-0 items-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#d9772c]"
                >
                    Découvrir les sessions disponibles
                    <ArrowRight className="h-4 w-4" />
                </Link>
            </section>

            <div className="inline-flex rounded-full border border-brand-line bg-white p-1 shadow-premium">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setTab(t.key)}
                        data-testid={`stagiaire-sessions-tab-${t.key}`}
                        className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === t.key ? "bg-brand-ink text-white" : "text-brand-ink hover:text-brand-orange"}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="mt-8">
                {loading ? (
                    <div className="text-brand-smoke">Chargement…</div>
                ) : filtered.length === 0 ? (
                    <div className="rounded-3xl border border-dashed border-brand-line bg-white p-10 text-center text-sm text-brand-smoke">
                        Aucune session dans cette catégorie.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {filtered.map((it) => {
                            const s = it.session;
                            const isPast = s.start_date < today;
                            return (
                                <Link
                                    key={it.id}
                                    to={`/stagiaire/sessions/${s.id}`}
                                    data-testid={`stagiaire-session-card-${s.id}`}
                                    className="group rounded-2xl border border-brand-line bg-white p-6 shadow-premium hover:-translate-y-0.5 hover:shadow-premium-lg transition-all"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="text-xs font-semibold uppercase tracking-wider text-brand-orange">
                                            {formatDate(s.start_date)}
                                        </div>
                                        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                                            it.status === "attended" ? "bg-green-100 text-green-700" :
                                            it.status === "confirmed" ? (isPast ? "bg-brand-line text-brand-smoke" : "bg-brand-orange/15 text-brand-orange") :
                                            "bg-brand-line text-brand-smoke"
                                        }`}>
                                            {it.status === "attended" ? "Présent" : it.status === "confirmed" ? (isPast ? "Passée" : "Confirmé") : it.status}
                                        </span>
                                    </div>
                                    <h3 className="mt-3 font-display text-xl text-brand-ink">{s.formation_name}</h3>
                                    <div className="mt-3 space-y-1.5 text-sm text-brand-ink/70">
                                        <div className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-brand-orange" /> {s.start_time}–{s.end_time}</div>
                                        <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-brand-orange" /> {s.city} · {s.venue_room_name}</div>
                                    </div>
                                    <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-ink group-hover:text-brand-orange">
                                        Voir le détail <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
